<template>
    <el-select ref="alo-ai-bots-selector"
               v-model="aloai_bot_id"
               v-loading="loading"
               class="w-full"
               :placeholder="placeholder"
               :multiple="multiple"
               :clearable="clearable"
               :collapse-tags="!no_collapse"
               :size="size"
               filterable
               default-first-option
               @change="selectAloAiBot">
        <template v-if="group_by_type">
            <el-option-group
                v-for="group in aloAiBotsGroupedByType"
                :key="`${group.type}-${group.direction}`"
                :label="group.label">
                <el-option 
                    v-for="bot in group.bots"
                    :key="bot.id"
                    :label="bot.name"
                    :value="bot.id"
                    :disabled="!bot.enabled">
                    <el-tooltip :disabled="bot.enabled"
                                content="This AI agent is disabled"
                                placement="left">
                        <span class="ml-2">{{ bot.name }}</span>
                    </el-tooltip>
                </el-option>
            </el-option-group>
        </template>
        <template v-else>
            <el-option v-for="bot in aloAiBotsAlphabeticalOrder"
                       :key="bot.id"
                       :label="bot.name"
                       :value="bot.id"
                       :disabled="!bot.enabled">
                <el-tooltip :disabled="bot.enabled"
                            content="This AI agent is disabled"
                            placement="left">
                    <span class="ml-2">{{ bot.name }}</span>
                </el-tooltip>
            </el-option>
        </template>
    </el-select>
</template>

<script>
import { clone } from 'lodash'
import { mapState } from 'vuex'
import * as AloAi from '../../constants/aloai'
import { aloai_mixin } from '../../mixins'

export default {
    mixins: [aloai_mixin],
    
    props: {
        value: {
            required: false,
            type: [Number, String]
        },

        multiple: {
            type: Boolean,
            required: false,
            default: false
        },

        no_collapse: {
            type: Boolean,
            required: false,
            default: false
        },

        clearable: {
            type: Boolean,
            required: false,
            default: false
        },

        size: {
            type: String,
            default: '',
            required: false
        },

        type: {
            type: Number,
            default: null,
            required: false
        },

        direction: {
            type: Number,
            default: null,
            required: false
        },

        group_by_type: {
            type: Boolean,
            required: false,
            default: false
        }
    },

    data() {
        return {
            aloai_bot_id: this.value,
            loading: false,
            aloai_bots: [],
        }
    },

    computed: {
        ...mapState('cache', [
            'current_company'
        ]),

        placeholder() {
            return this.multiple ? 'Select AloAi Agents' : 'Select AloAi Agent'
        },

        aloAiBotsAlphabeticalOrder() {
            let bots = clone(this.aloai_bots)

            return bots.sort((a, b) => {
                let textA = a.name.toUpperCase()
                let textB = b.name.toUpperCase()
                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
            })
        },

        aloAiBotsGroupedByType() {
            let bots = clone(this.aloai_bots)
            
            // Helper function to sort bots alphabetically by name
            const sortBotsByName = (bots) => {
                return bots.sort((a, b) => {
                    let textA = a.name.toUpperCase()
                    let textB = b.name.toUpperCase()
                    return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
                })
            }
            
            // Helper function to filter bots by type and direction
            const filterBots = (type, direction) => {
                let filtered = bots.filter(bot => bot.type === type && bot.direction === direction)
                
                // Special case for outbound voice bots
                if (type === AloAi.TYPE_VOICE && direction === AloAi.DIRECTION_OUTBOUND) {
                    if (!this.isAloAiOutboundVoiceAgentsEnabled()) {
                        return []
                    }
                }
                
                return sortBotsByName(filtered)
            }
            
            let groups = [
                { 
                    type: AloAi.TYPE_VOICE, 
                    direction: AloAi.DIRECTION_OUTBOUND,
                    label: 'Outbound Voice Agents', 
                    bots: filterBots(AloAi.TYPE_VOICE, AloAi.DIRECTION_OUTBOUND)
                },
                { 
                    type: AloAi.TYPE_VOICE, 
                    direction: AloAi.DIRECTION_INBOUND,
                    label: 'Inbound Voice Agents', 
                    bots: filterBots(AloAi.TYPE_VOICE, AloAi.DIRECTION_INBOUND)
                },
                { 
                    type: AloAi.TYPE_TEXT, 
                    direction: AloAi.DIRECTION_OUTBOUND,
                    label: 'Outbound Text Agents', 
                    bots: filterBots(AloAi.TYPE_TEXT, AloAi.DIRECTION_OUTBOUND)
                },
                { 
                    type: AloAi.TYPE_TEXT, 
                    direction: AloAi.DIRECTION_INBOUND,
                    label: 'Inbound Text Agents', 
                    bots: filterBots(AloAi.TYPE_TEXT, AloAi.DIRECTION_INBOUND)
                },
            ]

            return groups.filter(group => group.bots.length > 0)
        }
    },

    created() {
        VueEvent.listen('aloai_bot_created', (bot) => {
            this.addBot(bot)
        })
    },

    mounted() {
        if (this.isAloAiEnabled()) {
            this.getAloAiBots()
        }
    },

    methods: {
        selectAloAiBot(bot) {
            this.aloai_bot_id = bot
            this.$emit('change', bot)
        },

        async getAloAiBots() {
            this.loading = true
            this.aloai_bots = []
            let res = null
            let params = {
                page: 1,
                size: 50
            }

            if (this.type) {
                params.type = this.type === AloAi.TYPE_VOICE ? 'voice' : 'text'
            }

            if (this.direction) {
                params.direction = this.direction === AloAi.DIRECTION_OUTBOUND ? 'outbound' : 'inbound'
            }

            do {
                res = await axios.get('/api/v1/aloai/bot', {params: params})
                this.aloai_bots.push(...res.data.data)
                params.page++
            } while (res.data.pagination.next_page_url)

            this.loading = false
        },

        addBot(bot) {
            if (this.type && bot.type !== this.type) {
                return
            }
            if (this.direction && bot.direction !== this.direction) {
                return
            }

            // Check if bot already exists
            const exists = this.aloai_bots.find(b => b.id === bot.id)
            if (!exists) {
                this.aloai_bots.push(bot)
            }
        }
    },

    watch: {
        value() {
            this.aloai_bot_id = this.value
        }
    }
}
</script>
